import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import Select from '@atlaskit/select';
import { type Priority } from '@atlassian/eoc-priority-badge';
import { PriorityDropdown, type PriorityType } from '@atlassian/eoc-priority-dropdown';

import { BOOLEAN_FIELD, FIELD_MAPPINGS } from '../../../../common/constants';
import { ConditionFilterMessages } from '../../../../common/messages';
import { type IConditionFieldProps } from '../../../../common/types';

import { PRIORITY_MAP } from './constants';

const PriorityConditionFields: React.FC<IConditionFieldProps> = ({
	fieldType,
	update,
	operation: op,
	not,
	expectedValue,
	isDisabled,
	isReadOnly,
}) => {
	const { formatMessage } = useIntl();

	const { availableOperations, operation, priority } = useMemo(() => {
		const availableOperations = FIELD_MAPPINGS[fieldType].operations;
		const operation =
			[...availableOperations].find((_op) => _op.value === op) ?? FIELD_MAPPINGS[fieldType].default;
		const priority = PRIORITY_MAP[expectedValue as Priority] ?? PRIORITY_MAP.P3;
		return { availableOperations, operation, priority };
	}, [expectedValue, fieldType, op]);

	useEffect(() => {
		if (![...availableOperations].find((_op) => _op.value === op)) {
			update({ operation: FIELD_MAPPINGS[fieldType].default.value });
		}
	}, [availableOperations, fieldType, op, update]);

	// This effect is to ensure that the expected value is always a valid priority
	useEffect(() => {
		if (!PRIORITY_MAP[expectedValue as Priority]) {
			update({ expectedValue: PRIORITY_MAP.P3.value });
		}
	}, [expectedValue, update]);

	const bool = useMemo(() => BOOLEAN_FIELD.find((b) => b.value === not), [not]);

	return (
		<>
			<Select
				value={bool}
				onChange={(boolValue) => boolValue && update({ not: boolValue.value })}
				options={BOOLEAN_FIELD}
				isDisabled={isDisabled}
				menuIsOpen={isReadOnly ? false : undefined}
				isSearchable={!isReadOnly}
				// Make it appear over the top of the modal rather than scrollable
				menuPosition="fixed"
				label={formatMessage(ConditionFilterMessages.IsNotFieldAriaLabel)}
			/>
			<Select
				value={operation}
				options={availableOperations}
				onChange={(op) => op && update({ operation: op.value })}
				isDisabled={isDisabled}
				menuIsOpen={isReadOnly ? false : undefined}
				isSearchable={!isReadOnly}
				menuPosition="fixed"
				label={formatMessage(ConditionFilterMessages.ValueFieldAriaLabel)}
			/>
			<PriorityDropdown
				value={priority as PriorityType}
				isDisabled={isDisabled}
				isReadOnly={isReadOnly}
				onChange={(_priority) => _priority && update({ expectedValue: _priority.value })}
				menuPosition="fixed"
				showDynamicPriority={false}
				aria-label={formatMessage(ConditionFilterMessages.PriorityFieldAriaLabel)}
			/>
		</>
	);
};

export default PriorityConditionFields;
